package com.wintermar.inspection.feature.presentation.splash

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.wintermar.inspection.core.domain.FlowResult
import com.wintermar.inspection.core.ui.component.image.util.FileModel
import com.wintermar.inspection.core.ui.component.snackbar.util.SnackBarController
import com.wintermar.inspection.core.ui.component.snackbar.util.SnackBarVariant
import com.wintermar.inspection.feature.domain.service.PendingUploadCallback
import com.wintermar.inspection.feature.domain.service.PendingUploadService
import com.wintermar.inspection.feature.domain.service.SubmitRetryService
import com.wintermar.inspection.feature.domain.usecase.auth.GetAuthStatusUseCase
import com.wintermar.inspection.feature.domain.usecase.reference.GetListTemplateOptionUseCase
import com.wintermar.inspection.feature.domain.usecase.reference.GetListVesselUseCase
import com.wintermar.inspection.feature.domain.usecase.template.CollectTemplateUseCase
import com.wintermar.inspection.util.Constant
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.launch

class SplashViewModel(
    private val getAuthStatusUseCase: GetAuthStatusUseCase,
    private val collectTemplateUseCase: CollectTemplateUseCase,
    private val getListTemplateOptionUseCase: GetListTemplateOptionUseCase,
    private val getListVesselUseCase: GetListVesselUseCase,
    private val pendingUploadService: PendingUploadService,
    private val submitRetryService: SubmitRetryService
) : ViewModel() {

    val state = SplashState()

    private val _navigationEvent = Channel<SplashEvent>()
    val navigationEvent = _navigationEvent.receiveAsFlow()

    init {
        checkAuthStatus()
    }

    private fun checkAuthStatus() = viewModelScope.launch {
        delay(1000)
        if (!getAuthStatusUseCase()) {
            _navigationEvent.send(SplashEvent.NavigateToLogin)
            return@launch
        }

        collectData {
            pendingUpload()
            retryPendingSubmit()
            getTemplate().join()
            getOption().join()
            getVesselNames().join()
        }

        _navigationEvent.send(SplashEvent.NavigateToLanding)
    }

    private fun getTemplate() = viewModelScope.launch {
        collectTemplateUseCase()
            .collect { result ->
                if (result is FlowResult.Error) {
                    SnackBarController.show(
                        message = result.error?.message ?: Constant.UNKNOWN_ERROR,
                        variant = SnackBarVariant.ERROR
                    )

                    return@collect
                }

                if (result is FlowResult.Loading) {
                    state.isLoading = result.showLoading
                    return@collect
                }
            }
    }

    private fun getOption() = viewModelScope.launch {
        getListTemplateOptionUseCase().collect { result ->
            if (result is FlowResult.Error) {
                SnackBarController.show(
                    message = result.error?.message ?: Constant.UNKNOWN_ERROR,
                    variant = SnackBarVariant.ERROR
                )
            }
        }
    }

    private fun getVesselNames() = viewModelScope.launch {
        getListVesselUseCase().collect { result ->
            if (result is FlowResult.Error) {
                SnackBarController.show(
                    message = result.error?.message ?: Constant.UNKNOWN_ERROR,
                    variant = SnackBarVariant.ERROR
                )
            }
        }
    }

    private fun pendingUpload() = viewModelScope.launch {
        pendingUploadService.retryPendingUploads(
            callback = object : PendingUploadCallback {
                override fun onStarted(files: List<FileModel>) {}
                override fun onCompleted() {}
                override fun onError(error: Throwable?) {}
                override fun onNothingPending() {}
                override fun onSkipped(reason: String) {}
            }
        )
    }

    private fun retryPendingSubmit() = viewModelScope.launch {
        submitRetryService.trigger()
    }
}

expect suspend fun collectData(
    onCollect: suspend () -> Unit
)
